import axios from 'axios';
import React, { Component, ReactNode } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

interface ErrorBoundaryProps extends WithTranslation {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorData {
  error: string;
  message: string;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  baseUrl = `${import.meta.env.VITE_API_DOMAIN ? import.meta.env.VITE_API_DOMAIN : '/api'}`;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  getToken = (): string | null => {
    const token = localStorage?.getItem(`LOUISEBOT_token`);

    if (token) {
      return token.split('"').join('');
    }
    return null;
  };

  setError = async (data: ErrorData) => {
    try {
      const response = await axios.post(
        `${this.baseUrl}/auth/Error`,
        {
          ...data,
          applicationName: 'LouiseBotFrontend',
        },
        {
          headers: {
            'x-auth-token': this.getToken() || '',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending error report:', error);
      throw error;
    }
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, info);
    if (!window?.location?.hostname?.includes('localhost')) {
      this.setError({
        error: error.toString(),
        message: info.toString(),
      }).then();
    }
  }

  render() {
    const { t, children } = this.props;

    if (this.state.hasError && this.state.error) {
      return (
        <div className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-rounded-lg tw-bg-gray-50 dark:tw-bg-gray-800 tw-border tw-border-gray-200 dark:tw-border-gray-700">
          <svg className="tw-w-8 tw-h-8 tw-text-blue-500 tw-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="tw-font-medium tw-text-gray-800 dark:tw-text-gray-100 tw-text-sm">{t('ErrorBoundary/title')}</p>
            <p className="tw-text-gray-500 dark:tw-text-gray-400 tw-text-xs tw-mt-0.5">{t('ErrorBoundary/description')}</p>
          </div>
        </div>
      );
    }

    return children;
  }
}

// Export wrapped component (with translation support)
export default withTranslation()(ErrorBoundary);
